import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { updateJobPostingService } from "../../../services/employer/EmployerServices";
import { jobPostingShape } from "../../../shapes/JobPostingShape";
import JobPostingQuestionLabel from "./JobPostingQuestionLabel";
import {
  Dropdown,
  Modal,
  Button,
  Grid,
  Card,
  Container,
  Form,
} from "semantic-ui-react";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { markdownToDraft, draftToMarkdown } from "markdown-draft-js";
import { EditorState, convertFromRaw, convertToRaw } from "draft-js";
import {
  getSuccessToastWithMessage,
  getFailureToastWithMessage,
} from "../../shared/toast/ToastOptions";
import { jobPostingDescriptionToolbar } from "./jobPostingDescriptionToolbar";

const EditJobPostingModal = (props) => {
  const [jobPostingGeneralInfo, setJobPostingGeneralInfo] = useState({
    id: props.jobPosting.id,
    employerId: props.employerId,
    role: props.jobPosting.role,
    team: props.jobPosting.team,
    dateCreated: props.jobPosting.dateCreated,
  });
  const [jobPostingDescription, setJobPostingDescription] = useState(() =>
    EditorState.createWithContent(
      convertFromRaw(markdownToDraft(props.jobPosting.description))
    )
  );

  const [jobPostingLocation, setJobPostingLocation] = useState(
    props.jobPosting.location
  );

  const [
    jobPostingFieldIdsMappedToRequiredFlag,
    setJobPostingFieldIdsMappedToRequiredFlag,
  ] = useState(() => {
    const selectedJobPostingFieldIdsMappedToRequiredFlag = {};
    props.jobPosting.fields.map((field) => {
      selectedJobPostingFieldIdsMappedToRequiredFlag[
        field.fieldId
      ] = !!field.required;
    });
    return selectedJobPostingFieldIdsMappedToRequiredFlag;
  });

  useEffect(() => {
    setJobPostingGeneralInfo({
      id: props.jobPosting.id,
      employerId: props.employerId,
      role: props.jobPosting.role,
      team: props.jobPosting.team,
      dateCreated: props.jobPosting.dateCreated,
    });
    setJobPostingFieldIdsMappedToRequiredFlag(() => {
      const selectedJobPostingFieldIdsMappedToRequiredFlag = {};
      props.jobPosting.fields.map((field) => {
        selectedJobPostingFieldIdsMappedToRequiredFlag[field.fieldId] =
          field.required;
      });
      return selectedJobPostingFieldIdsMappedToRequiredFlag;
    });
  }, [props.jobPosting]);

  useEffect(() => {
    setJobPostingLocation(jobPostingLocation);
  }, [jobPostingLocation]);

  useEffect(() => {
    setJobPostingDescription(jobPostingDescription);
  }, [jobPostingDescription]);

  const jobPostingFieldOptions = () => {
    return props.jobPostingFields.map((field) => {
      return {
        key: field.id,
        value: field.id,
        text: field.value,
      };
    });
  };

  const handleJobPostingFieldChange = (event, data) => {
    const currentJobPostingFields = {};
    data["value"].forEach((fieldId) => {
      if (!(fieldId in jobPostingFieldIdsMappedToRequiredFlag)) {
        currentJobPostingFields[fieldId] = false;
      } else {
        currentJobPostingFields[fieldId] =
          jobPostingFieldIdsMappedToRequiredFlag[fieldId];
      }
    });
    setJobPostingFieldIdsMappedToRequiredFlag(currentJobPostingFields);
  };

  const makeJobPostingFieldRequired = (fieldId) => {
    const currentJobPostingFieldIdsMappedToRequiredFlag = {
      ...jobPostingFieldIdsMappedToRequiredFlag,
    };
    if (currentJobPostingFieldIdsMappedToRequiredFlag[fieldId]) {
      currentJobPostingFieldIdsMappedToRequiredFlag[fieldId] = false;
    } else {
      currentJobPostingFieldIdsMappedToRequiredFlag[fieldId] = true;
    }
    setJobPostingFieldIdsMappedToRequiredFlag(
      currentJobPostingFieldIdsMappedToRequiredFlag
    );
  };

  const handleJobPostingChange = (event, setJobPostingInfo, jobPostingInfo) => {
    setJobPostingInfo({
      ...jobPostingInfo,
      [event.target.name]: event.target.value,
    });
  };

  const handleSaveButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    updateJobPostingService({
      generalInfo: {
        ...jobPostingGeneralInfo,
        description: draftToMarkdown(
          convertToRaw(jobPostingDescription.getCurrentContent())
        ),
      },
      location: jobPostingLocation,
      jobPostingFields: formatJobPostingFields(),
    })
      .then((response) => {
        response.json().then((data) => {
          if (data["hasError"]) {
            getFailureToastWithMessage(
              "Failed to edit job posting, please try again."
            );
            console.log(data["errorMessage"]);
            props.setShowEditJobPostingModal(false);
          } else if (data["success"]) {
            getSuccessToastWithMessage("Successfully edited job posting!");
            window.location.assign("employer-console");
          } else {
            getFailureToastWithMessage(
              "Failed to edit job posting, please try again."
            );
          }
        });
      })
      .catch((response) => {
        console.log(response);
        getFailureToastWithMessage(
          "Failed to edit job posting, please try again."
        );
      });
  };

  const formatJobPostingFields = () => {
    return Object.keys(jobPostingFieldIdsMappedToRequiredFlag).map((id) => {
      return { id, required: jobPostingFieldIdsMappedToRequiredFlag[id] };
    });
  };

  const removeJobPostingField = (fieldId) => {
    const currentJobPostingFieldIdsMappedToRequiredFlag = {
      ...jobPostingFieldIdsMappedToRequiredFlag,
    };
    delete currentJobPostingFieldIdsMappedToRequiredFlag[fieldId];
    setJobPostingFieldIdsMappedToRequiredFlag(
      currentJobPostingFieldIdsMappedToRequiredFlag
    );
  };

  const renderItemContent = (item) => {
    return (
      <JobPostingQuestionLabel
        questionTitle={item.text}
        value={item.value}
        checked={!!jobPostingFieldIdsMappedToRequiredFlag[item.value]}
        removeJobPostingField={removeJobPostingField}
        makeJobPostingFieldRequired={makeJobPostingFieldRequired}
      />
    );
  };

  return (
    <Modal
      onClose={() => props.setShowEditJobPostingModal(false)}
      onOpen={() => props.setShowEditJobPostingModal(true)}
      open={props.showEditJobPostingModal}
      closeIcon="cancel"
    >
      <Modal.Header>Edit Job Posting</Modal.Header>
      <Modal.Content>
        <Form
          onSubmit={(event) => {
            handleSaveButtonClick(event);
          }}
        >
          <Card fluid>
            <Card.Content>
              <Card.Header>Location</Card.Header>
              <Form.Group widths="equal">
                <Form.Field
                  name="city"
                  label="City"
                  required
                  control="input"
                  value={jobPostingLocation.city}
                  onChange={(event) => {
                    handleJobPostingChange(
                      event,
                      setJobPostingLocation,
                      jobPostingLocation
                    );
                  }}
                  placeholder="Enter a City"
                />

                <Form.Field
                  label="State"
                  name="state"
                  required
                  control="input"
                  value={jobPostingLocation.state}
                  onChange={(event) => {
                    handleJobPostingChange(
                      event,
                      setJobPostingLocation,
                      jobPostingLocation
                    );
                  }}
                  placeholder="Enter a State"
                />
                <Form.Field
                  label="Zip Code"
                  name="zipCode"
                  required
                  control="input"
                  value={jobPostingLocation.zipCode}
                  onChange={(event) => {
                    handleJobPostingChange(
                      event,
                      setJobPostingLocation,
                      jobPostingLocation
                    );
                  }}
                  placeholder="Enter a Zip Code"
                />
              </Form.Group>
            </Card.Content>
          </Card>
          <Card fluid>
            <Card.Content>
              <Card.Header>General Info</Card.Header>
              <Grid>
                <Grid.Row columns={2}>
                  <Grid.Column>
                    <Form.Field
                      name="role"
                      label="Job Role"
                      required
                      control="input"
                      value={jobPostingGeneralInfo.role}
                      onChange={(event) => {
                        handleJobPostingChange(
                          event,
                          setJobPostingGeneralInfo,
                          jobPostingGeneralInfo
                        );
                      }}
                      placeholder="Enter a Job Role"
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <Form.Field
                      label="Team"
                      name="team"
                      control="input"
                      value={jobPostingGeneralInfo.team}
                      onChange={(event) => {
                        handleJobPostingChange(
                          event,
                          setJobPostingGeneralInfo,
                          jobPostingGeneralInfo
                        );
                      }}
                      placeholder="Enter a Team"
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row columns={1}>
                  <Grid.Column>
                    <Card label="Job Description" fluid>
                      <Editor
                        editorState={jobPostingDescription}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        onEditorStateChange={setJobPostingDescription}
                        toolbar={jobPostingDescriptionToolbar}
                      />
                    </Card>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Card.Content>
          </Card>
          <Card fluid>
            <Card.Content>
              <Card.Header>Job Posting Questions</Card.Header>
              <Dropdown
                placeholder="Select Job Posting Questions"
                fluid
                search
                selection
                multiple
                value={Object.keys(jobPostingFieldIdsMappedToRequiredFlag).map(
                  Number
                )}
                renderLabel={renderItemContent}
                onChange={handleJobPostingFieldChange}
                options={jobPostingFieldOptions()}
              />
            </Card.Content>
          </Card>
          <Container textAlign="center">
            <Button.Group attached="bottom">
              <Button positive>Save</Button>
              <Button.Or />
              <Button
                negative
                onClick={() => {
                  props.setShowEditJobPostingModal(false);
                }}
              >
                Cancel
              </Button>
            </Button.Group>
          </Container>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

EditJobPostingModal.propTypes = {
  showEditJobPostingModal: PropTypes.bool.isRequired,
  setShowEditJobPostingModal: PropTypes.func.isRequired,
  jobPosting: jobPostingShape.isRequired,
  employerId: PropTypes.string.isRequired,
};

EditJobPostingModal.defaultProps = {};

export default EditJobPostingModal;
